/**
 * Hydrate + paginate the message history of a single conversation.
 *
 *   const { messages, hasMoreOlder, fetchOlder, isLoading, error } =
 *     useConversationMessages(conversationId)
 *
 * Pipeline:
 *   1. On first mount / id change: REST-seed the most-recent page via
 *      `listMessages(conversationId, { limit: 50 })` → store via
 *      `seedWireMessages`.
 *   2. Live events keep the list fresh — `useMessengerSubscription` joins
 *      `conversation:<id>` (typically from `useConversationList`); this
 *      hook just reads the store slice.
 *   3. `fetchOlder()` paginates with the stored cursor; the store's
 *      `prependOlderMessages` flips the server's newest-first page into
 *      the rendered oldest-first order.
 *
 * Memo'd per-conversation seed-state means re-opening a thread is free
 * (no re-fetch) — fresh content arrives via the channel.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import type { Message as WireMessage, Paginated } from '../services/messenger.wire'
import { markDeliveredUpTo, type ParticipantCursor, type WireHistoryPage } from '../services/messenger.socket'
import { messengerApi } from '../services/messenger.api'
import { trace } from '../services/trace'
import { useMessengerStore } from '../store/messenger.store'
import { useAuthStore } from '@/features/auth/store/auth.store'

const EMPTY_PAGE: WireHistoryPage = { items: [], nextCursor: null, hasMore: false, cursors: [] }

interface UseConversationMessages {
  messages:     WireMessage[]
  hasMoreOlder: boolean
  isLoading:    boolean
  error:        string | null
  fetchOlder:   () => Promise<void>
}

const seededOnce = new Set<string>()

/**
 * Module-level frozen-empty array so the selector returns the **same
 * reference** on every render when the store has nothing for this
 * conversation yet. Without this, `useMemo` consumers (and any
 * `useEffect([messages])`) churn refs and re-fire indefinitely.
 */
const EMPTY_MESSAGES: WireMessage[] = []

export function useConversationMessages(
  conversationId: string | null | undefined,
): UseConversationMessages {
  const messages = useMessengerStore((s) =>
    conversationId ? s.wireMessagesByConv.get(conversationId) ?? EMPTY_MESSAGES : EMPTY_MESSAGES,
  )
  const cursor = useMessengerStore((s) =>
    conversationId ? s.messageCursors.get(conversationId) : undefined,
  )
  const seed     = useMessengerStore((s) => s.seedWireMessages)
  const prepend  = useMessengerStore((s) => s.prependOlderMessages)
  const seedCursors = useMessengerStore((s) => s.seedCursors)
  const me       = useAuthStore((s) => s.user)
  // "From someone else?" test keys on contactPresenceId (senderIds are keyed on
  // it). The socket/token identity for the delivered-cursor push stays `me.id`.
  const presenceId = useAuthStore((s) => s.presenceId)

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError]         = useState<string | null>(null)

  // History reads go DIRECT to the SDK server REST endpoint (client → sdk-server
  // → Postgres), NOT over the socket. The socket stays for LIVE events only.
  // Same `{items, nextCursor, hasMore, cursors}` shape; sdk-server also hydrates
  // reactions + attachments on the page.
  const loadPage = useCallback(
    async (convId: string, opts: { before?: string; limit?: number }): Promise<WireHistoryPage> => {
      if (!me?.id) return EMPTY_PAGE
      const page = (await messengerApi.listMessages(convId, {
        before: opts.before,
        limit: opts.limit,
      })) as Paginated<WireMessage> & { cursors?: ParticipantCursor[] }
      return {
        items: page.items,
        nextCursor: page.nextCursor,
        hasMore: page.hasMore,
        cursors: page.cursors ?? [],
      }
    },
    [me?.id],
  )

  // Module-scoped seededOnce keeps re-mounts of the same conversation cheap;
  // an explicit refresh path is the caller's responsibility.
  const inFlightRef = useRef<boolean>(false)

  useEffect(() => {
    if (!conversationId) return
    if (seededOnce.has(conversationId)) return
    if (inFlightRef.current) return

    inFlightRef.current = true
    setIsLoading(true)
    setError(null)
    let cancelled = false

    trace('render', `open thread  conv=${conversationId.slice(0, 8)} → seeding newest page (limit 50)`)
    loadPage(conversationId, { limit: 50 })
      .then((page) => {
        if (cancelled) return
        trace('store', `seedWireMessages  conv=${conversationId.slice(0, 8)}  ${page.items.length} messages, hasMore(older)=${page.hasMore}`)
        seed(conversationId, page.items, { nextCursor: page.nextCursor, hasMore: page.hasMore })
        // Seed participants' delivered/read cursors so ticks render on load.
        seedCursors(conversationId, page.cursors)
        // Mark delivered up to the newest message from someone else — covers
        // messages that arrived while we were offline and only sync now.
        if (me?.id) {
          let newestOtherAt: string | null = null
          for (const m of page.items) {
            if (m.senderId && m.senderId !== presenceId && (!newestOtherAt || m.createdAt > newestOtherAt)) {
              newestOtherAt = m.createdAt
            }
          }
          if (newestOtherAt) markDeliveredUpTo(me.id, conversationId, newestOtherAt, me.name)
        }
        seededOnce.add(conversationId)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'failed to load messages')
      })
      .finally(() => {
        if (cancelled) return
        inFlightRef.current = false
        setIsLoading(false)
      })

    return () => { cancelled = true }
  }, [conversationId, seed, loadPage])

  const fetchOlder = useCallback(async (): Promise<void> => {
    if (!conversationId) return
    if (!cursor?.hasMore || !cursor.nextCursor || isLoading) return
    trace('render', `scroll up → fetchOlder  conv=${conversationId.slice(0, 8)}  before=${cursor.nextCursor.slice(0, 12)} (older page of 50)`)
    setIsLoading(true)
    setError(null)
    try {
      const page = await loadPage(conversationId, {
        limit: 50,
        before: cursor.nextCursor,
      })
      trace('store', `prependOlderMessages  conv=${conversationId.slice(0, 8)}  +${page.items.length} older, hasMore=${page.hasMore}`)
      prepend(conversationId, page.items, { nextCursor: page.nextCursor, hasMore: page.hasMore })
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'failed to load older')
    } finally {
      setIsLoading(false)
    }
  }, [conversationId, cursor?.hasMore, cursor?.nextCursor, isLoading, prepend, loadPage])

  return {
    messages,
    hasMoreOlder: cursor?.hasMore ?? false,
    isLoading,
    error,
    fetchOlder,
  }
}

/**
 * Drop the seeded-once flag for a conversation — used after explicit
 * "refresh thread" actions, or on logout-and-back-in flows.
 */
export function _forgetMessageSeed(conversationId: string): void {
  seededOnce.delete(conversationId)
}

/** Wipe all seed flags. Pair with `resetWire` on logout. */
export function _forgetAllMessageSeeds(): void {
  seededOnce.clear()
}
